import { Box, Chip, Divider, Stack } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import { Palette } from 'Palette';
import { StyledTypography } from '../styles/styles';
import { CHANGE_COLORS, renderInlineDiff, renderValue } from './contentDiffRenderers';
import { ContentDiffPayload } from './contentDiffTypes';
import { makePathHumanReadable } from './contentDiffViewerUtils';

export type { ContentDiffPayload } from './contentDiffTypes';
export { extractContentDiffPayload } from './contentDiffViewerUtils';

interface ContentDiffViewerProps {
  diff?: ContentDiffPayload | null;
  tone?: keyof typeof Palette;
}

const ContentDiffViewer: React.FC<ContentDiffViewerProps> = ({ diff, tone = 'default' }) => {
  if (!diff || !diff.entries) {
    return (
      <StyledTypography variant="body2" color="text.secondary">
        No diff data available.
      </StyledTypography>
    );
  }

  const { type, fromLabel, toLabel, generatedAt, summary, entries } = diff;

  const formattedGeneratedAt = generatedAt
    ? (() => {
        try {
          return format(new Date(generatedAt), 'MMM dd, yyyy HH:mm');
        } catch {
          return generatedAt;
        }
      })()
    : undefined;

  return (
    <Stack spacing={2} sx={{ fontSize: '0.875rem' }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {type && (
            <Chip
              size="small"
              color="default"
              label={`${type.toUpperCase()} diff`}
              sx={{ textTransform: 'capitalize' }}
            />
          )}
          {fromLabel && toLabel && (
            <StyledTypography variant="body2" color="text.secondary">
              {fromLabel} → {toLabel}
            </StyledTypography>
          )}
          {formattedGeneratedAt && (
            <StyledTypography variant="caption" color="text.secondary">
              Generated {formattedGeneratedAt}
            </StyledTypography>
          )}
        </Stack>
        {summary && summary.total > 0 ? (
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
            <Chip size="small" label={`Total: ${summary.total}`} />
            <Chip size="small" color="success" label={`Added: ${summary.added}`} />
            <Chip size="small" color="error" label={`Removed: ${summary.removed}`} />
            <Chip size="small" color="info" label={`Changed: ${summary.changed}`} />
          </Stack>
        ) : summary ? (
          <StyledTypography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No changes detected.
          </StyledTypography>
        ) : null}
      </Box>

      <Divider sx={{ borderColor: Palette[tone].border }} />

      <Stack spacing={2}>
        {entries.map((entry, index) => {
          const chipColors = CHANGE_COLORS[entry.changeType];
          return (
            <Box
              key={`${entry.path}-${index}`}
              sx={{
                border: `1px solid ${Palette[tone].border}`,
                borderRadius: 1,
                p: 1.5,
                backgroundColor: Palette[tone].background,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <StyledTypography
                  variant="subtitle2"
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                >
                  {makePathHumanReadable(entry.path)}
                </StyledTypography>
                <Chip
                  size="small"
                  label={entry.changeType.toUpperCase()}
                  sx={{
                    backgroundColor: chipColors.bg,
                    color: chipColors.text,
                    fontWeight: 600,
                  }}
                />
                <Chip size="small" variant="outlined" label={entry.valueType} />
              </Stack>

              {entry.inlineDiff ? (
                <Box sx={{ mt: 1 }}>{renderInlineDiff(entry)}</Box>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <StyledTypography variant="caption" color="text.secondary">
                      Before
                    </StyledTypography>
                    {renderValue(entry, 'before')}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <StyledTypography variant="caption" color="text.secondary">
                      After
                    </StyledTypography>
                    {renderValue(entry, 'after')}
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default ContentDiffViewer;
